/**
 * Cookie Storage Backend
 *
 * Stores data in browser cookies with support for domain, path, secure, and sameSite options.
 * Falls back to memory storage if cookies are unavailable.
 */

import { MemoryBackend } from './memory';
import type { StorageBackend, StorageBackendOptions } from './types';

export class CookieBackend implements StorageBackend {
  private fallback: MemoryBackend | null = null;
  private defaultOptions: StorageBackendOptions;

  constructor(defaultOptions: StorageBackendOptions = {}) {
    this.defaultOptions = {
      path: '/',
      sameSite: 'lax',
      ...defaultOptions,
    };
  }

  get(key: string): string | null {
    if (!this.isSupported()) {
      return this.getFallback().get(key);
    }

    try {
      const name = `${encodeURIComponent(key)}=`;
      const cookies = document.cookie.split(';');

      for (let cookie of cookies) {
        cookie = cookie.trim();
        if (cookie.startsWith(name)) {
          return decodeURIComponent(cookie.substring(name.length));
        }
      }

      return null;
    } catch (error) {
      console.warn('Cookie get failed:', error);
      return this.getFallback().get(key);
    }
  }

  set(key: string, value: string, options?: StorageBackendOptions): void {
    if (!this.isSupported()) {
      this.getFallback().set(key, value);
      return;
    }

    try {
      const opts = { ...this.defaultOptions, ...options };
      const parts: string[] = [`${encodeURIComponent(key)}=${encodeURIComponent(value)}`];

      // Add expiration if TTL is provided
      if (opts.ttl) {
        const expires = new Date();
        expires.setTime(expires.getTime() + opts.ttl * 1000);
        parts.push(`expires=${expires.toUTCString()}`);
      }

      if (opts.path) {
        parts.push(`path=${opts.path}`);
      }

      if (opts.domain) {
        parts.push(`domain=${opts.domain}`);
      }

      if (opts.secure) {
        parts.push('secure');
      }

      if (opts.sameSite) {
        parts.push(`samesite=${opts.sameSite}`);
      }

      document.cookie = parts.join('; ');
    } catch (error) {
      console.warn('Cookie set failed, falling back to memory:', error);
      this.getFallback().set(key, value);
    }
  }

  remove(key: string): void {
    if (!this.isSupported()) {
      this.getFallback().remove(key);
      return;
    }

    try {
      // Set cookie with past expiration date to delete it
      const opts = this.defaultOptions;
      const parts: string[] = [
        `${encodeURIComponent(key)}=`,
        'expires=Thu, 01 Jan 1970 00:00:00 UTC',
      ];

      if (opts.path) {
        parts.push(`path=${opts.path}`);
      }

      if (opts.domain) {
        parts.push(`domain=${opts.domain}`);
      }

      document.cookie = parts.join('; ');
    } catch (error) {
      console.warn('Cookie remove failed:', error);
      this.getFallback().remove(key);
    }
  }

  clear(): void {
    if (!this.isSupported()) {
      this.getFallback().clear();
      return;
    }

    try {
      // Get all cookie names and delete them
      const cookies = document.cookie.split(';');

      for (let cookie of cookies) {
        cookie = cookie.trim();
        const eqPos = cookie.indexOf('=');
        const name = eqPos > -1 ? cookie.substring(0, eqPos) : cookie;
        this.remove(decodeURIComponent(name));
      }
    } catch (error) {
      console.warn('Cookie clear failed:', error);
      this.getFallback().clear();
    }
  }

  isSupported(): boolean {
    try {
      // Check if cookies are enabled
      if (typeof document === 'undefined' || !document.cookie) {
        return false;
      }

      // Try to set and remove a test cookie
      const testKey = '__cookie_test__';
      document.cookie = `${testKey}=test; path=/`;
      const supported = document.cookie.indexOf(testKey) !== -1;

      // Clean up test cookie
      document.cookie = `${testKey}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/`;

      return supported;
    } catch {
      return false;
    }
  }

  private getFallback(): MemoryBackend {
    if (!this.fallback) {
      this.fallback = new MemoryBackend();
    }
    return this.fallback;
  }
}
